import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MOBILE_METRICS from '../styles/mobile-metrics';

/**
 * 데스크톱 확장 레이아웃을 위한 커스텀 훅
 * 
 * 모바일 디자인을 유지하면서 데스크톱에서 공간을 활용하기 위한 유틸리티 제공
 * 
 * @param {Object} options 설정 옵션
 * @param {boolean} options.enableMobilePanels - 모바일 패널 관리 활성화 여부
 * @param {string} options.initialPanel - 초기 모바일 패널
 * @param {string} options.basePath - 기본 경로 (URL 네비게이션용)
 * @param {Object} options.expandConfig - 데스크톱 확장 설정
 * @returns {Object} 반응형 관련 상태와 함수들
 */
export const useDesktopExpanded = (options = {}) => {
  const { 
    enableMobilePanels = false, 
    initialPanel = 'panel1',
    basePath = '',
    expandConfig = {
      mode: 'none', // 'horizontal', 'grid', 'none'
      maintainMobileWidth: true,
      columns: 'repeat(auto-fit, minmax(320px, 1fr))'
    }
  } = options;

  // URL 파라미터 및 네비게이션
  const params = useParams();
  const navigate = useNavigate();

  // 모바일/데스크톱 뷰 상태
  const [isMobileView, setIsMobileView] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < MOBILE_METRICS.BREAKPOINTS.mobile;
  });
  
  const [windowWidth, setWindowWidth] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth : 1024;
  });
  
  const [isInitialized, setIsInitialized] = useState(false);
  
  // 현재 모바일 패널 상태
  const [currentMobilePanel, setCurrentMobilePanel] = useState(initialPanel);
  
  // 뷰포트 정보
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === 'undefined') return '100vh';
    return `${window.innerHeight}px`;
  });

  // 디바이스 정보
  const deviceInfo = useMemo(() => {
    if (typeof window === 'undefined') return { isIOS: false, isIPhone: false, isSafari: false };
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isIPhone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    return { isIOS, isIPhone, isSafari };
  }, []);

  // 반응형 처리
  useEffect(() => {
    let timeoutId = null;
    
    const handleResize = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      timeoutId = setTimeout(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        setWindowWidth(width);
        setIsMobileView(width < MOBILE_METRICS.BREAKPOINTS.mobile);
        setViewportHeight(`${height}px`);
        setIsInitialized(true);
      }, 100);
    };

    // 초기화
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    setWindowWidth(width);
    setIsMobileView(width < MOBILE_METRICS.BREAKPOINTS.mobile);
    setViewportHeight(`${height}px`);
    setIsInitialized(true);

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    if (deviceInfo.isIOS && deviceInfo.isSafari) {
      window.addEventListener('scroll', handleResize);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (deviceInfo.isIOS && deviceInfo.isSafari) {
        window.removeEventListener('scroll', handleResize);
      }
    };
  }, [deviceInfo.isIOS, deviceInfo.isSafari]);

  // 모바일 스타일 생성 함수
  const getMobileStyle = useCallback((baseStyle = {}) => {
    return {
      ...baseStyle,
      // 모바일 메트릭스 적용
      padding: `${MOBILE_METRICS.SPACING.page.mobile}px`,
      fontSize: MOBILE_METRICS.TYPOGRAPHY.fontSize.base,
      // 추가 모바일 최적화
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale'
    };
  }, []);

  // 데스크톱 확장 스타일 생성 함수
  const getDesktopExpandedStyle = useCallback((baseStyle = {}, areaConfig = {}) => {
    if (isMobileView) {
      return getMobileStyle(baseStyle);
    }

    // 데스크톱에서도 모바일 스타일 기본값 유지
    const mobileBaseStyle = getMobileStyle(baseStyle);

    // 영역별 확장 설정
    const expandedStyle = {
      ...mobileBaseStyle,
      // 너비 제한 (모바일 너비 유지 옵션)
      maxWidth: areaConfig.maintainMobileWidth ? '428px' : areaConfig.maxWidth || 'none',
      width: areaConfig.width || 'auto',
      // 추가 데스크톱 최적화
      transition: `all ${MOBILE_METRICS.ANIMATION.transition.default} ${MOBILE_METRICS.ANIMATION.easing.default}`
    };

    return expandedStyle;
  }, [isMobileView, getMobileStyle]);

  // 컴포넌트 클래스 생성 함수
  const getComponentClasses = useCallback((baseClasses = '', componentType = '') => {
    const classes = [baseClasses];

    // 모바일 우선 클래스
    classes.push('mobile-first');

    // 데스크톱 확장 클래스
    if (!isMobileView) {
      classes.push('desktop-expanded');
      classes.push(`desktop-${componentType}`);
    }

    return classes.filter(Boolean).join(' ');
  }, [isMobileView]);

  // 레이아웃 구성 생성 함수
  const createLayoutConfig = useCallback((areas = {}) => {
    if (isMobileView || expandConfig.mode === 'none') {
      return {
        mode: 'stack',
        areas: areas
      };
    }

    // 데스크톱 레이아웃 구성
    return {
      mode: expandConfig.mode,
      areas: Object.entries(areas).reduce((acc, [key, area]) => {
        acc[key] = {
          ...area,
          config: {
            maintainMobileWidth: expandConfig.maintainMobileWidth,
            ...area.config
          }
        };
        return acc;
      }, {})
    };
  }, [isMobileView, expandConfig]);

  // 페이지네이션 활성화 여부
  const shouldUsePagination = useCallback(() => {
    return !isMobileView; // 데스크톱에서만 페이지네이션 사용
  }, [isMobileView]);

  // 모바일 패널 관리 함수들
  const switchToPanel = useCallback((panelName) => {
    if (enableMobilePanels && isMobileView) {
      setCurrentMobilePanel(panelName);
    }
  }, [enableMobilePanels, isMobileView]);

  const handleMobileBack = useCallback(() => {
    if (enableMobilePanels && currentMobilePanel !== initialPanel) {
      setCurrentMobilePanel(initialPanel);
    } else {
      window.history.back();
    }
  }, [enableMobilePanels, currentMobilePanel, initialPanel]);

  return {
    // 기본 상태
    isMobileView,
    windowWidth,
    isInitialized,
    viewportHeight,
    
    // 디바이스 정보
    ...deviceInfo,
    
    // 스타일 생성 함수
    getMobileStyle,
    getDesktopExpandedStyle,
    getComponentClasses,
    
    // 레이아웃 설정
    createLayoutConfig,
    expandConfig,
    
    // 페이지네이션
    shouldUsePagination,
    
    // 모바일 패널 관리 (enableMobilePanels가 true일 때만)
    ...(enableMobilePanels && {
      currentMobilePanel,
      setCurrentMobilePanel,
      switchToPanel,
      handleMobileBack
    }),
    
    // 유틸리티
    isDesktop: !isMobileView,
    isMobile: isMobileView,
    
    // 메트릭스 접근
    metrics: MOBILE_METRICS,
    
    // CSS 변수 생성
    getCSSVariables: () => ({
      '--mobile-view': isMobileView ? '1' : '0',
      '--window-width': `${windowWidth}px`,
      '--viewport-height': viewportHeight,
      // 모바일 메트릭스 CSS 변수
      '--mobile-page-padding': `${MOBILE_METRICS.SPACING.page.mobile}px`,
      '--mobile-gap-default': `${MOBILE_METRICS.SPACING.gap.default}px`,
      '--mobile-card-radius': MOBILE_METRICS.COMPONENTS.card.borderRadius,
      '--mobile-button-height': `${MOBILE_METRICS.COMPONENTS.button.height.default}px`
    })
  };
};